import { useContext, useEffect, useState } from 'react';
import _ from 'underscore';

import getConfig from 'next/config';
import { getEntityUrl } from '@culture/utils/helpers/entities';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';
import BalloonLayout from 'components/YandexMap/BaloonLayout';
import ClustererContentLayout from 'components/YandexMap/ClustererContentLayout';
import ClusterBalloonContentLayout from 'components/YandexMap/ClusterBalloonContentLayout';
import PlacemarkLayout from 'components/YandexMap/PlacemarkLayout';
import { YandexMapsContext } from 'contexts/YandexMapsContext';
import { ImageFormatContext } from 'contexts/ImageFormatContext';

const {
	publicRuntimeConfig: {
		assetPrefix,
		entities: { manorsRubric }
	}
} = getConfig();

const getPlacemarkOptions = (ymaps) => ({
	balloonLayout: BalloonLayout(ymaps),
	balloonShadow: false,
	balloonPanelMaxMapArea: 0,
	balloonOffset: [-150, 7],
	iconOffset: [-13, -35],
	iconShape: {
		type: 'Rectangle',
		coordinates: [
			[0, 0],
			[26, 35]
		]
	},
	iconLayout: PlacemarkLayout(ymaps)
});
const getClustererOptions = (ymaps) => ({
	groupByCoordinates: false,
	clusterDisableClickZoom: false,
	clusterHideIconOnBalloonOpen: true,
	geoObjectHideIconOnBalloonOpen: false,
	clusterIconLayout: ClustererContentLayout(ymaps),
	clusterBalloonContentLayout: ClusterBalloonContentLayout(ymaps),
	clusterIconShape: {
		type: 'Circle',
		coordinates: [0, 0],
		radius: 15
	}
});
const getPointProperties = (entitiy, imageFormat, entityType) => {
	const initialAddress =
		entitiy?.address || (entitiy?.locale && entitiy?.locale?.title);

	const address = entitiy?.addressComment
		? `${initialAddress} (${entitiy?.addressComment})`
		: initialAddress;

	const thumbnailImage =
		entitiy?.thumbnailFile &&
		getImageUrl(entitiy.thumbnailFile, {
			imageFormat,
			params: {
				crop: 'fill',
				gravity: 'center',
				width: 144,
				height: 144
			}
		});

	const isManor = _(entitiy?.rubricPaths).contains(manorsRubric);
	const manorThumbnail =
		thumbnailImage || `${assetPrefix}images/institutes/default_manor.png`;

	return {
		title: entitiy.title,
		address,
		url: entityType && entitiy?.name ? getEntityUrl(entityType, entitiy) : null,
		thumbnailImg: isManor ? manorThumbnail : thumbnailImage
	};
};

export const useYandexMapsLayout = (mapRef, items, entityType) => {
	const { ymaps, loading } = useContext(YandexMapsContext);
	const imageFormat = useContext(ImageFormatContext);
	const [mapInstance, setMapInstance] = useState(null);
	const [mapLoading, setMapLoading] = useState(true);

	const reCreateMap = () => {
		if (!_.isNull(mapInstance)) {
			mapInstance.destroy();
		}
		const RussiaCenterCoordinates = [94.25, 66.41];
		const map = new ymaps.Map(mapRef.current, {
			center: RussiaCenterCoordinates,
			zoom: 3,
			behaviors: ['default'],
			controls: ['zoomControl', 'fullscreenControl']
		});
		map.events.add('click', () => {
			map.balloon.close();
		});

		return map;
	};

	useEffect(() => {
		if (ymaps && mapRef.current && !loading && items.length > 0) {
			setMapLoading(true);
			const newMapInstance = reCreateMap();
			setMapInstance(newMapInstance);
			setMapLoading(false);
		}
	}, [loading, items]);

	useEffect(() => {
		if (mapInstance) {
			const geoObjects = [...items].map(
				(item) =>
					new ymaps.Placemark(
						item.location.coordinates,
						getPointProperties(item, imageFormat, entityType),
						getPlacemarkOptions(ymaps)
					)
			);

			const isSingleItem = items.length === 1;

			const clusterer = new ymaps.Clusterer(getClustererOptions(ymaps));
			clusterer.add(geoObjects);

			clusterer.options.set({
				gridSize: 80,
				clusterDisableClickZoom: false
			});
			mapInstance.geoObjects.add(clusterer);
			mapInstance.setBounds(clusterer.getBounds(), {
				checkZoomRange: !isSingleItem,
				zoomMargin: 10
			});
			if (isSingleItem) {
				mapInstance.setZoom(17);
			}
		}
	}, [mapInstance, mapLoading]);

	return { mapInstance, mapLoading: loading || mapLoading, ymaps };
};
